import { Component, Input, Output, EventEmitter, OnChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MediaItem, SUPPORTED_MEDIA } from '../models/message';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-media-preview',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: '../templates/media-preview.component.html',
  styleUrls: ['../templates/media-preview.scss'],
})
export class MediaPreviewComponent implements OnChanges {
  @Input() media: MediaItem[] = [];
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Input() showFilesize = true;
  @Input() showFilename = false;
  @Input() showCloseButton = false;

  @Output() remove = new EventEmitter<number>();
  @Output() mediaLoaded = new EventEmitter<void>();

  mediaUrls: SafeUrl[] = [];
  rawUrls: string[] = [];
  loaded: boolean[] = [];

  private mediaUrlCache = new Map<string, SafeUrl>();
  private loadedCount = 0;
  private sanitizer = inject(DomSanitizer);

  ngOnChanges() {
    console.log('[media-preview] ngOnChanges called, media:', this.media);
    console.log('[media-preview] media length:', this.media?.length);
    if (this.media?.length > 0) {
      console.log('[media-preview] first item:', JSON.stringify(this.media[0], null, 2));
    }
    let changed = false;
    this.loaded = this.media.map(() => false);
    this.rawUrls = this.media.map(item => {
      let url = item.url;
      
      // Handle different URL formats
      if (!url.startsWith('http')) {
        // If it's a relative path, construct full URL
        if (url.startsWith('/')) {
          url = `https://${environment.messagingApi}${url}`;
        } else {
          // If it doesn't start with / or http, it might be a domain path
          url = `https://${url}`;
        }
      }
      return url;
    });
    
    this.media.forEach((_, index) => {
      const urlToUse = this.rawUrls[index];
      if (!this.mediaUrlCache.has(urlToUse)) {
        this.mediaUrlCache.set(urlToUse, this.sanitizer.bypassSecurityTrustUrl(urlToUse));
        changed = true;
      }
    });
    
    if (changed || this.mediaUrls.length !== this.media.length) {
      this.mediaUrls = this.rawUrls.map(url => this.mediaUrlCache.get(url)!);
    }
    this.loadedCount = 0;
  }

  getPlaceholderDimensions(): { width: number; height: number } {
    switch (this.size) {
      case 'small': return { width: 80, height: 80 };
      case 'medium': return { width: 150, height: 150 };
      case 'large': return { width: 300, height: 300 };
      default: return { width: 100, height: 100 };
    }
  }

  onImageLoad(index: number) {
    console.log(`Image ${index} loaded successfully`);
    this.loaded[index] = true;
    this.loadedCount++;
    if (this.loadedCount >= this.mediaUrls.length) {
      setTimeout(() => this.mediaLoaded.emit(), 50);
    }
  }

  getDisplayedFilename(item: MediaItem): { name: string; ext: string } {
    try {
      const mediaDef = SUPPORTED_MEDIA.find(m => m.mime === item.contentType);
      const ext = mediaDef?.extension ?? this.extractExtensionFromFilename(item.filename);

      const dotIndex = item.filename.lastIndexOf('.');
      let name = dotIndex === -1 ? item.filename : item.filename.substring(0, dotIndex);

      if (name.length > 12) name = name.substring(0, 12) + '...';
      return { name, ext };
    } catch {
      return { name: 'unknown', ext: '' };
    }
  }

  private extractExtensionFromFilename(filename: string): string {
    const parts = filename.split('.');
    return parts.length > 1 ? `.${parts.pop()}` : '';
  }

  private getContentTypeFromUrl(url: string): string | null {
    try {
      const urlObj = new URL(url);
      const responseContentType = urlObj.searchParams.get('response-content-type');
      console.log('[media-preview] getContentTypeFromUrl:', {
        urlStart: url.substring(0, 80),
        responseContentType
      });
      return responseContentType;
    } catch (e) {
      console.log('[media-preview] getContentTypeFromUrl error:', e);
      return null;
    }
  }

  isUrlExpired(item: MediaItem): boolean {
    try {
      const urlObj = new URL(item.url);
      const amzDate = urlObj.searchParams.get('X-Amz-Date');
      const amzExpires = urlObj.searchParams.get('X-Amz-Expires');

      if (!amzDate || !amzExpires) {
        // Not a presigned URL or missing params - assume valid
        return false;
      }

      // Parse X-Amz-Date format: 20251219T200640Z
      const year = parseInt(amzDate.substring(0, 4));
      const month = parseInt(amzDate.substring(4, 6)) - 1;
      const day = parseInt(amzDate.substring(6, 8));
      const hour = parseInt(amzDate.substring(9, 11));
      const minute = parseInt(amzDate.substring(11, 13));
      const second = parseInt(amzDate.substring(13, 15));

      const createdAt = new Date(Date.UTC(year, month, day, hour, minute, second));
      const expiresInSeconds = parseInt(amzExpires);
      const expirationTime = new Date(createdAt.getTime() + expiresInSeconds * 1000);

      return new Date() > expirationTime;
    } catch {
      return false;
    }
  }

  private getEffectiveContentType(item: MediaItem): string | null {
    console.log('[media-preview] getEffectiveContentType called with item:', {
      url: item.url?.substring(0, 80),
      contentType: item.contentType,
      filename: item.filename
    });
    // First try the item's contentType
    if (item.contentType) {
      console.log('[media-preview] using item.contentType:', item.contentType);
      return item.contentType;
    }
    // Fall back to parsing from presigned URL
    const fromUrl = this.getContentTypeFromUrl(item.url);
    console.log('[media-preview] fallback to URL result:', fromUrl);
    return fromUrl;
  }

  isImage(item: MediaItem): boolean {
    const contentType = this.getEffectiveContentType(item);
    const result = contentType ? SUPPORTED_MEDIA.some(m => m.mime === contentType && m.category === 'image') : false;
    console.log('[media-preview] isImage:', { contentType, result });
    return result;
  }

  isPdf(item: MediaItem): boolean {
    const contentType = this.getEffectiveContentType(item);
    const result = contentType ? SUPPORTED_MEDIA.some(m => m.mime === contentType && m.extension === '.pdf') : false;
    console.log('[media-preview] isPdf:', { contentType, result });
    return result;
  }

  trackByMediaUrl(index: number, item: MediaItem) {
    return item.url;
  }

  getDisplayedFilesize(size: number): string {
    if (isNaN(size)) return '';
    const i = Math.floor(Math.log(size) / Math.log(1024));
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    return (size / Math.pow(1024, i)).toFixed(1) + ' ' + sizes[i];
  }

  removeFile(index: number) {
    this.remove.emit(index);
  }
}
